import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { approach, systemRoles, systems, workTogetherExamples } from '../data/about';
import { easeOutStrong, fadeUp } from '../utils/motion';
const inlineLink = 'text-navy font-semibold underline decoration-line underline-offset-4 hover:decoration-navy transition-colors duration-150';
export function AboutPage() {
  return <>
      {/* Hero */}
      <section className="bg-white border-b border-line pt-32 pb-20 lg:pt-44 lg:pb-28" data-id="element-13" data-mp="1fu1wyal40btt">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: easeOutStrong
      }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-14" data-mp="1avqn3zute1bl">
          <div className="lg:col-span-5" data-id="element-15" data-mp="3rm73wvtg332x">
            <p className="text-sm font-medium text-teal mb-5" data-id="element-16" data-mp="3rn0fca3cmgre">Company</p>
            <h1 className="font-heading font-extrabold text-5xl lg:text-7xl text-ink-dark leading-[1.03] tracking-tight" data-id="element-17" data-mp="3mnniqxmodvbm">
              About SyNeural
            </h1>
          </div>
          <div className="lg:col-span-7 lg:pt-10" data-id="element-18" data-mp="3pteuloqmim2n">
            <p className="text-2xl text-ink-dark leading-relaxed mb-6" data-id="element-19" data-mp="3urggehffozht">
              SyNeural builds systems that help people develop sound judgment,
              researchers connect ideas, and organizations turn knowledge into
              action.
            </p>
            <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-20" data-mp="8blymm0xeekr">
              Our work brings together three complementary parts: the{' '}
              <a href="/semantic-knowledge" className={inlineLink} data-id="element-21" data-mp="hctmsb9q9z14">
                Semantic Knowledge Platform
              </a>
              , which connects and governs knowledge; the{' '}
              <a href="/human-capability" className={inlineLink} data-id="element-22" data-mp="2j46m8ksxfeps">
                Human Capability Framework
              </a>
              , which defines assessment and development through CJI and NCJI;
              and{' '}
              <a href="/noetivra" className={inlineLink} data-id="element-23" data-mp="3reyr4v9wgkaa">
                Noetivra
              </a>
              , which makes these resources accessible through a visual,
              interactive workspace.
            </p>
            <p className="text-lg text-ink-dark leading-relaxed border-l-2 border-teal pl-5" data-id="element-24" data-mp="4aiw2gxrml32">
              We believe intelligence grows when people can think clearly,
              knowledge can move across disciplines, and institutions can learn
              from their decisions.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Mission */}
      <section className="bg-surface-bg py-20 lg:py-28" data-id="element-25" data-mp="mynvjc7e3jw2">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-26" data-mp="iy3wg339a67a">
          <h2 className="lg:col-span-5 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight" data-id="element-27" data-mp="wyesrlnc4czu">
            Our mission
          </h2>
          <div className="lg:col-span-7" data-id="element-28" data-mp="1bk2cx6tjfhip">
            <p className="font-heading font-bold text-2xl lg:text-3xl text-ink-dark leading-snug mb-6" data-id="element-29" data-mp="1bl5136zh75ux">
              SyNeural’s mission is to strengthen human capability and make
              knowledge more useful in the world.
            </p>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-30" data-mp="16l8y9b2ilkgy">
              We build systems that help people explore ideas, understand
              complex problems, develop their strengths, and make decisions they
              can explain and improve. Our work connects scientific discovery
              with practical applications in education, employment, clinical
              research, civic life, and leadership.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Systems and frameworks */}
      <section className="bg-white py-20 lg:py-28" data-id="element-31" data-mp="9wb49v2l9qku">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-32" data-mp="dwtvbmwnqslz">
          <motion.h2 {...fadeUp} className="font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight mb-14" data-id="element-33" data-mp="dwbndt0mupy8">
            Our systems and frameworks
          </motion.h2>
          <div className="border-b border-line" data-id="element-34" data-mp="1wwljeo8tucw">
            {systems.map(system => <motion.article key={system.name} {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 border-t border-line py-12" data-id="element-35" data-mp="3t753dpsczaky">
                <div className="lg:col-span-4" data-id="element-36" data-mp="3k66zd7hx732d">
                  <h3 className="font-heading font-bold text-2xl lg:text-3xl text-ink-dark mb-3" data-id="element-37" data-mp="3f8f3y3i2gytf">
                    {system.name}
                  </h3>
                  {system.tagline && <p className="text-teal font-medium leading-snug" data-id="element-38" data-mp="35cc3r36rza94">{system.tagline}</p>}
                </div>
                <div className="lg:col-span-8 space-y-5" data-id="element-39" data-mp="18i7kwpt2tqjl">
                  {system.paragraphs.map((paragraph, index) => <p key={paragraph} className={`leading-relaxed ${index === 0 ? 'text-xl text-ink-dark' : 'text-lg text-ink-muted'}`} data-id="element-40" data-mp="1c0wnsov4d99u" data-mpi={index}>
                      {paragraph}
                    </p>)}
                  {system.note && <p className="text-base text-ink-dark leading-relaxed bg-surface-bg border-l-2 border-teal px-5 py-4 rounded-r-md" data-id="element-41" data-mp="1psuqagubs6x1">
                      {system.note}
                    </p>}
                  <a href={system.link} className="inline-flex items-center gap-2 pt-1 text-navy font-semibold hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-42" data-mp="x2vyidoqxvoo">
                    {system.linkLabel}
                    <ArrowRight size={16} aria-hidden="true" data-id="element-43" data-mp="2dif8uymkh25s" />
                  </a>
                </div>
              </motion.article>)}
          </div>
        </div>
      </section>

      {/* How they work together */}
      <section className="bg-navy-deep text-white py-20 lg:py-28" data-id="element-44" data-mp="1srjrq69sp7rz">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-45" data-mp="1nu9tc14fhls0">
          <motion.h2 {...fadeUp} className="font-heading font-bold text-4xl lg:text-5xl tracking-tight leading-tight mb-12" data-id="element-46" data-mp="1nvusk41otsw7">
            How they work together
          </motion.h2>
          <motion.p {...fadeUp} className="-mt-6 mb-12 max-w-4xl text-xl text-white/80 leading-relaxed" data-id="element-47" data-mp="1a2d7getcs842">
            SKP connects and governs knowledge. The Human Capability Framework
            defines assessment and development through CJI and NCJI. Noetivra
            makes these resources accessible through interactive views,
            assessments, and workflows.
          </motion.p>

          <motion.dl {...fadeUp} className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/15 border border-white/15 rounded-lg overflow-hidden" data-id="element-48" data-mp="rbvqf7hvv0yj">
            {systemRoles.map(item => <div key={item.name} className="bg-navy-deep p-6 lg:p-8" data-id="element-49" data-mp="2xzupqztpihma">
                <dt className="text-sm text-teal font-medium mb-3" data-id="element-50" data-mp="32qung7anh3pu">{item.name}</dt>
                <dd className="font-heading font-bold text-xl leading-snug" data-id="element-51" data-mp="32sxix6uknp1y">{item.role}</dd>
              </div>)}
          </motion.dl>

          <motion.div {...fadeUp} className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-10" data-id="element-52" data-mp="2t09tix5x8nd9">
            {workTogetherExamples.map(example => <div key={example.who} className="border-t border-white/20 pt-6" data-id="element-53" data-mp="3goj5769f7uqo">
                <h3 className="font-heading font-bold text-lg mb-3" data-id="element-54" data-mp="3lp6zq1wuytpn">{example.who}</h3>
                <p className="text-white/75 leading-relaxed" data-id="element-55" data-mp="37p5xai0y3e82">That could mean {example.body.charAt(0).toLowerCase() + example.body.slice(1)}</p>
              </div>)}
          </motion.div>

          <motion.p {...fadeUp} className="mt-14 max-w-4xl text-lg text-white leading-relaxed border-l-2 border-green-soft pl-6" data-id="element-56" data-mp="1w9zf5bof61qs">
            Each application would have its own methods, permissions, and
            validation requirements. Sensitive personal records would remain in
            controlled stores, with access appropriate to their purpose.
          </motion.p>
        </div>
      </section>

      {/* Vision */}
      <section className="bg-white py-20 lg:py-28" data-id="element-57" data-mp="2e0fo2ji8iuzi">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-58" data-mp="2e20go3kzo3uq">
          <h2 className="lg:col-span-5 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight" data-id="element-59" data-mp="36ol9z9l0yq0x">
            Our vision
          </h2>
          <div className="lg:col-span-7" data-id="element-60" data-mp="31vs2idy693l5">
            <p className="font-heading font-bold text-2xl lg:text-3xl text-ink-dark leading-snug mb-6" data-id="element-61" data-mp="2wzti11fys5q0">
              SyNeural’s long-term vision is a global infrastructure for human
              capability and connected knowledge.
            </p>
            <p className="text-lg text-ink-muted leading-relaxed mb-5" data-id="element-62" data-mp="3plmfqwmeku5x">
              We see a future where researchers can discover connections across
              fields, people have clearer pathways to develop their strengths,
              and institutions can examine the reasoning behind their decisions.
            </p>
            <p className="text-lg text-ink-muted leading-relaxed mb-5" data-id="element-63" data-mp="esz7n8hgposk">
              That vision extends from individual learning to collaboration
              across organizations and public systems. It requires both useful
              technology and accountable institutions: people who approve
              methods, review outcomes, correct mistakes, and take
              responsibility for consequential decisions.
            </p>
            <p className="text-lg text-ink-dark leading-relaxed" data-id="element-64" data-mp="yfwwfjth2p1h">
              The goal is to expand what people are capable of understanding and
              accomplishing together.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Leadership */}
      <section className="bg-surface-bg py-20 lg:py-28 border-y border-line" data-id="element-65" data-mp="2a7hgxemjjyd8">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-66" data-mp="2f5j9cq5uxa1d">
          <h2 className="lg:col-span-5 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight" data-id="element-67" data-mp="2k2sw1y1v37li">
            Leadership
          </h2>
          <div className="lg:col-span-7 flex flex-col sm:flex-row gap-8" data-id="element-68" data-mp="2sxz659860btu">
            <img src="https://cdn.magicpatterns.com/uploads/aGzifJuZ6XFUAFRAawshw8/IMG_1407.jpg" alt="Portrait of Shawn Blake, Lead Developer and Founder of SyNeural" width={160} height={200} loading="lazy" className="w-32 h-40 sm:w-40 sm:h-48 shrink-0 rounded-lg object-cover object-[50%_30%] border border-line bg-surface-muted" data-id="element-69" data-mp="2xwaibqlxmyf8" />
            <div data-id="element-70" data-mp="1dfnpebeojht7">
              <h3 className="font-heading font-bold text-2xl text-ink-dark" data-id="element-71" data-mp="1dh8kc24wp2rl">Shawn Blake</h3>
              <p className="text-teal font-medium mb-5" data-id="element-72" data-mp="3qriclzy9aw4a">Lead Developer &amp; Founder</p>
              <p className="text-lg text-ink-muted leading-relaxed" data-id="element-73" data-mp="3lyxl552tov3x">
                Shawn Blake leads SyNeural’s work across cognitive systems,
                semantic knowledge, and institutional design with a recorded IQ
                of 185. His focus is
                bringing these disciplines together to help researchers connect
                ideas, people develop their capabilities, and organizations turn
                complex knowledge into responsible action.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Approach */}
      <section className="bg-white py-20 lg:py-28" data-id="element-74" data-mp="3vql6co8sdfwy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-75" data-mp="3vrw8ykqhwhmh">
          <motion.h2 {...fadeUp} className="lg:col-span-5 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight" data-id="element-76" data-mp="2kamsm72ny5pe">
            The SyNeural approach
          </motion.h2>
          <motion.dl {...fadeUp} className="lg:col-span-7 border-t border-line" data-id="element-77" data-mp="2qreqqc4xgcos">
            {approach.map(item => <div key={item.title} className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-8 py-6 border-b border-line" data-id="element-78" data-mp="2cqc4nzdxn2dv">
                <dt className="sm:col-span-4 font-heading font-bold text-lg text-ink-dark" data-id="element-79" data-mp="230sk8pakn0gw">{item.title}</dt>
                <dd className="sm:col-span-8 text-ink-muted leading-relaxed" data-id="element-80" data-mp="27samrhs8t93z">{item.body}</dd>
              </div>)}
          </motion.dl>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy text-white py-20 lg:py-28" data-id="element-81" data-mp="1y1pojvghe6ll">
        <motion.div {...fadeUp} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-82" data-mp="1xzv4ejd1aiik">
          <h2 className="font-heading font-bold text-4xl lg:text-5xl tracking-tight mb-6" data-id="element-83" data-mp="1k4sqpa1y3ov7">
            Build with SyNeural
          </h2>
          <p className="text-xl text-white/80 leading-relaxed mb-10" data-id="element-84" data-mp="15f7xjw2vp966">
            Whether you are connecting scientific research, developing human
            capability, or helping organizations collaborate and make better
            decisions, SyNeural is building the systems and frameworks to
            support that work.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-85" data-mp="1adj3hvq1yzj4">
            <a href="/#what-we-build" className="inline-flex items-center justify-center gap-2 bg-white text-navy hover:bg-surface-muted font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-86" data-mp="1odaq2kumxo8q">
              Explore our systems
              <ArrowRight size={18} aria-hidden="true" data-id="element-87" data-mp="1t6n27vi4o2kl" />
            </a>
            <a href="/partnerships" className="inline-flex items-center justify-center border border-white/40 text-white hover:bg-white/10 font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-88" data-mp="274lfexkppsyi">
              Start a partnership conversation
            </a>
          </div>
        </motion.div>
      </section>
    </>;
}